/**
 * 運営ウォレット管理サービス
 * ミント用ウォレットの残高確認・配分・管理
 */

import { ethers } from 'ethers';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from './firebase';

// ========================================
// 型定義
// ========================================

export interface MintWallet {
  index: number;
  walletAddress: string;
  privateKey: string;
  mnemonic?: string[];
  isValid: boolean;
  createdAt: Date | null;
}

export interface WalletWithBalance extends MintWallet {
  balance: string;
  nonce: number;
}

// ========================================
// RPC URL
// ========================================

const isProduction = window.location.hostname === 'nft.runport.run';
const SONEIUM_RPC_URL = isProduction
  ? 'https://rpc.soneium.org' // mainnet
  : 'https://rpc.minato.soneium.org'; // testnet

// ========================================
// ウォレット取得
// ========================================

/** 全ウォレット取得 */
export async function getWallets(): Promise<MintWallet[]> {
  const walletsRef = collection(db, 'wallets');
  const q = query(walletsRef, orderBy('createdAt'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      index: Number(docData.id),
      walletAddress: data.walletAddress,
      privateKey: data.privateKey,
      mnemonic: data.mnemonic,
      isValid: data.isValid ?? true,
      createdAt: data.createdAt?.toDate() ?? null,
    };
  });
}

/** ウォレットの残高・nonce を取得（バッチ処理） */
export async function getWalletBalances(wallets: MintWallet[]): Promise<WalletWithBalance[]> {
  const provider = new ethers.JsonRpcProvider(SONEIUM_RPC_URL);
  const BATCH_SIZE = 5;
  const results: WalletWithBalance[] = [];

  // 5件ずつバッチ処理（RPC レート制限対策）
  for (let i = 0; i < wallets.length; i += BATCH_SIZE) {
    const batch = wallets.slice(i, i + BATCH_SIZE);

    const batchResults = await Promise.all(
      batch.map(async (wallet) => {
        try {
          const [balanceWei, nonce] = await Promise.all([
            provider.getBalance(wallet.walletAddress),
            provider.getTransactionCount(wallet.walletAddress),
          ]);

          const balance = ethers.formatEther(balanceWei);

          return {
            ...wallet,
            balance: parseFloat(balance).toFixed(5),
            nonce,
          };
        } catch (error) {
          console.error(`Failed to get balance for ${wallet.walletAddress}:`, error);
          return {
            ...wallet,
            balance: 'Error',
            nonce: -1,
          };
        }
      })
    );

    results.push(...batchResults);

    // 次のバッチまで少し待つ
    if (i + BATCH_SIZE < wallets.length) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  return results;
}

// ========================================
// ウォレット操作
// ========================================

/** ウォレットの有効/無効を切り替え */
export async function updateWalletIsValid(index: number, isValid: boolean): Promise<void> {
  const walletRef = doc(db, 'wallets', String(index));
  await updateDoc(walletRef, {
    isValid,
  });
}

/** ウォレット追加（HDウォレット派生） */
export async function addWallets(mnemonic: string, startIndex: number, count: number): Promise<void> {
  const hdPath = "m/44'/60'/0'/0/";
  const batch = writeBatch(db);

  for (let i = 0; i < count; i++) {
    const newIndex = startIndex + i;
    const accountPath = hdPath + newIndex;
    const hdNode = ethers.HDNodeWallet.fromPhrase(mnemonic, undefined, accountPath);

    // wallets に追加
    batch.set(doc(db, 'wallets', String(newIndex)), {
      walletAddress: hdNode.address,
      privateKey: hdNode.privateKey,
      isValid: true,
      createdAt: serverTimestamp(),
      deletedAt: null,
    });

    // mintWorkers に追加
    batch.set(doc(db, 'mintWorkers', String(newIndex)), {
      status: 'waiting',
      taskId: null,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

/** ウォレットリセット（index > 0 を削除） */
export async function resetWallets(): Promise<void> {
  const walletsRef = collection(db, 'wallets');
  const mintWorkersRef = collection(db, 'mintWorkers');

  const [walletDocs, mintWorkerDocs] = await Promise.all([getDocs(walletsRef), getDocs(mintWorkersRef)]);

  const batch = writeBatch(db);

  // index > 0 のウォレットを削除
  for (const docData of walletDocs.docs) {
    if (docData.id !== '0') {
      batch.delete(docData.ref);
    }
  }

  // mintWorkers を全削除
  for (const docData of mintWorkerDocs.docs) {
    batch.delete(docData.ref);
  }

  await batch.commit();
}

// ========================================
// 残高配分
// ========================================

/** 配分中のタスクがあるか確認 */
export async function hasPendingTransfers(): Promise<boolean> {
  const transferQueueRef = collection(db, 'transferTokenQueue');
  // pending または processing のタスクがあるかチェック
  const [pendingSnapshot, processingSnapshot] = await Promise.all([
    getDocs(query(transferQueueRef, where('status', '==', 'pending'))),
    getDocs(query(transferQueueRef, where('status', '==', 'processing'))),
  ]);
  return pendingSnapshot.size > 0 || processingSnapshot.size > 0;
}

/** 残高配分を開始 */
export async function startDistribution(wallets: WalletWithBalance[], masterWalletAddress: string): Promise<void> {
  // 有効なウォレットのみ対象
  const validWallets = wallets.filter((w) => w.isValid);

  // 全ウォレットの残高合計
  const totalBalance = validWallets.reduce((sum, w) => {
    const balance = parseFloat(w.balance);
    return sum + (Number.isNaN(balance) ? 0 : balance);
  }, 0);

  // 平均残高
  const averageBalance = totalBalance / validWallets.length;

  // 配分タスクを作成
  const batch = writeBatch(db);
  let count = 0;

  for (const wallet of validWallets) {
    if (wallet.index === 0) continue; // マスターウォレットはスキップ

    const currentBalance = parseFloat(wallet.balance);
    if (Number.isNaN(currentBalance)) continue;

    const transferAmount = averageBalance - currentBalance;
    if (transferAmount <= 0) continue; // 足りてる場合はスキップ

    const queueRef = doc(collection(db, 'transferTokenQueue'));
    batch.set(queueRef, {
      from: masterWalletAddress,
      to: wallet.walletAddress,
      chain: 'soneium',
      transferTokenAmount: parseFloat(transferAmount.toFixed(18)),
      status: 'pending',
      createdAt: serverTimestamp(),
    });

    count++;
    // 500件ごとにコミット
    if (count % 500 === 0) {
      await batch.commit();
    }
  }

  if (count % 500 !== 0) {
    await batch.commit();
  }

  // Cloud Function を呼び出して転送開始
  const adminStartTransferToken = httpsCallable(functions, 'adminStartTransferToken');
  await adminStartTransferToken({ data: null });
}

/** 配分後の予想残高を計算 */
export function calculateAverageBalance(wallets: WalletWithBalance[]): number {
  const validWallets = wallets.filter((w) => w.isValid);

  const totalBalance = validWallets.reduce((sum, w) => {
    const balance = parseFloat(w.balance);
    return sum + (Number.isNaN(balance) ? 0 : balance);
  }, 0);

  return totalBalance / validWallets.length;
}

// ========================================
// 任意アドレスへの送金
// ========================================

/** 任意のウォレットにトークンを送金 */
export async function sendTokenToAddress(
  masterWalletAddress: string,
  toAddress: string,
  amount: number
): Promise<void> {
  const queueRef = doc(collection(db, 'transferTokenQueue'));
  await setDoc(queueRef, {
    from: masterWalletAddress,
    to: toAddress,
    chain: 'soneium',
    transferTokenAmount: amount,
    status: 'pending',
    createdAt: serverTimestamp(),
  });

  const adminStartTransferToken = httpsCallable(functions, 'adminStartTransferToken');
  await adminStartTransferToken({ data: null });
}
